// ParameterFormBar — yeni-design styled parameter inputs for any ReportConfig.
//
// Renders a responsive grid of inputs based on each parameter's type:
//   date    → <input type="date">
//   int/decimal/float/double/number → <input type="number">
//   bool/boolean → <Checkbox>
//   string  → <Input> (yeni-design)
//
// Submit triggers the parent's `onSubmit(values)`. Reset returns values to
// the defaults derived by `computeDefaults`. localStorage persistence is
// handled by the parent (GenericReportPage) so this component stays presentational.
import { useState, type ChangeEvent, type FormEvent } from 'react';
import dayjs from 'dayjs';
import { Button, Input } from './yeni-design/primitives';
import { I } from './yeni-design/icons';
import type { ParameterConfig, ParameterUiType } from '../types/panelConfig';

export type ParamValues = Record<string, string>;

/**
 * Computes opinionated defaults for each parameter:
 *  - explicit `default` override wins (sentinel "today" → today's date for date params)
 *  - date: today (YYYY-MM-DD)
 *  - number-like: "0"
 *  - bool: "false"
 *  - string: "" (empty — sproc auto-default behavior preserved; user can override)
 */
export function computeDefaults(parameters: ParameterConfig[]): ParamValues {
  const today = dayjs().format('YYYY-MM-DD');
  const out: ParamValues = {};
  for (const p of parameters) {
    if (p.default != null && p.default !== '') {
      // "today" sentinel for date params (sector seed convention)
      if (isDateType(p.type) && p.default.toLowerCase() === 'today') {
        out[p.key] = today;
      } else {
        out[p.key] = p.default;
      }
      continue;
    }
    if (isDateType(p.type)) out[p.key] = today;
    else if (isNumericType(p.type)) out[p.key] = '0';
    else if (isBoolType(p.type)) out[p.key] = 'false';
    else out[p.key] = '';
  }
  return out;
}

export function isDateType(t: ParameterUiType): boolean {
  return t === 'date';
}
export function isNumericType(t: ParameterUiType): boolean {
  return t === 'int' || t === 'decimal' || t === 'float' || t === 'double' || t === 'number';
}
export function isBoolType(t: ParameterUiType): boolean {
  return t === 'bool' || t === 'boolean';
}

interface ParameterFormBarProps {
  parameters: ParameterConfig[];
  values: ParamValues;
  onChange: (values: ParamValues) => void;
  onSubmit: () => void;
  onReset?: () => void;
  isLoading?: boolean;
  rowCount?: number;
}

export function ParameterFormBar({
  parameters,
  values,
  onChange,
  onSubmit,
  onReset,
  isLoading,
  rowCount,
}: ParameterFormBarProps) {
  // local touched state — submit is gated only by required-empty for the moment
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  if (parameters.length === 0) {
    return (
      <div className="flex items-center gap-2 px-4 py-2.5 bg-surface border border-border rounded-md text-[12px] text-ink-500 mb-3">
        <I.Filter size={12} />
        <span>Bu rapor için filtre yok.</span>
        {typeof rowCount === 'number' && (
          <span className="ml-auto">
            <strong className="tnum text-ink-900">{rowCount}</strong> kayıt
          </span>
        )}
      </div>
    );
  }

  const update = (key: string, value: string) => {
    onChange({ ...values, [key]: value });
    setTouched((t) => ({ ...t, [key]: true }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-surface border border-border rounded-md p-3 mb-3"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {parameters.map((p) => (
          <ParameterField
            key={p.key}
            param={p}
            value={values[p.key] ?? ''}
            onChange={(v) => update(p.key, v)}
            invalid={p.required && touched[p.key] && (values[p.key] ?? '').trim() === ''}
          />
        ))}
      </div>
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
        {typeof rowCount === 'number' && !isLoading && (
          <span className="text-[12px] text-ink-500">
            <strong className="tnum text-ink-900">{rowCount}</strong> kayıt
          </span>
        )}
        {isLoading && (
          <span className="text-[12px] text-ink-500 inline-flex items-center gap-1.5">
            <I.Refresh size={12} className="animate-spin" />
            Sorgu çalıştırılıyor…
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {onReset && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onReset}
              disabled={isLoading}
            >
              Sıfırla
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            size="sm"
            icon={I.Search}
            disabled={isLoading}
          >
            Sorgula
          </Button>
        </div>
      </div>
    </form>
  );
}

interface ParameterFieldProps {
  param: ParameterConfig;
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
}

function ParameterField({ param, value, onChange, invalid }: ParameterFieldProps) {
  const labelText = param.label && param.label.trim().length > 0 ? param.label : param.key;
  const labelClass = `block text-[11px] font-medium uppercase tracking-wider text-ink-500 mb-1 ${
    invalid ? 'text-neg-700' : ''
  }`;

  return (
    <label className="block min-w-0">
      <span className={labelClass}>
        {labelText}
        {param.required && <span className="text-neg-600 ml-0.5">*</span>}
      </span>
      {renderInput(param, value, onChange)}
    </label>
  );
}

function renderInput(
  param: ParameterConfig,
  value: string,
  onChange: (v: string) => void,
) {
  const handle = (e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value);

  if (isDateType(param.type)) {
    return (
      <input
        type="date"
        value={value}
        onChange={handle}
        className="w-full bg-white border border-border rounded-md px-3 h-9 text-[13px] text-ink-900 focus-ring focus:border-brand-600 outline-none"
      />
    );
  }
  if (isNumericType(param.type)) {
    return (
      <input
        type="number"
        value={value}
        onChange={handle}
        placeholder="0"
        className="w-full bg-white border border-border rounded-md px-3 h-9 text-[13px] text-ink-900 placeholder:text-ink-400 focus-ring focus:border-brand-600 outline-none mono"
      />
    );
  }
  if (isBoolType(param.type)) {
    const checked = value === 'true' || value === '1';
    return (
      <div className="flex items-center h-9 gap-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
          className="w-4 h-4 rounded border-border focus-ring"
        />
        <span className="text-[13px] text-ink-700">{checked ? 'Evet' : 'Hayır'}</span>
      </div>
    );
  }
  // string fallback
  return (
    <Input
      value={value}
      onChange={handle}
      placeholder="(boş — tüm kayıtlar)"
      size="md"
    />
  );
}
